import { isSafeRedirectTo } from '@/lib/oauth2/redirect-validator';

/**
 * 任意の文字列を「安全な内部パス」に正規化する。Open Redirect 防御。
 *
 * `redirect` クエリパラメータや sessionStorage に積まれた戻り先 URL など、
 * 外部由来の文字列をルーター遷移に渡す前に必ず通す。許可するのは現在の origin 配下の
 * 相対パスのみで、絶対 URL / protocol-relative (`//`) / backslash / URL エンコード
 * 経由のトラバーサル / 制御文字 / 先頭空白は全て拒否する。
 *
 * @returns 正規化済みの `pathname + search + hash`。安全でなければ `null`。
 */
export function toSafeInternalPath(value: string | null | undefined): string | null {
  if (!value) return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  if (/[\\\r\n\t]/.test(value)) return null;
  if (/^\/%(2f|5c)/i.test(value)) return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

/**
 * 内部パスまたは外部許可URLとして「安全な遷移先」を返す。Open Redirect / XSS 防御。
 *
 * Kratos の `redirectTo`（外部 IdP の絶対 URL か、フロー失効時の内部相対パス
 * `/auth/sign-in` のいずれも取りうる）のように、内部・外部の両方を取りうる値を
 * `window.location` 系シンクへ渡す前に通す。内部パスを優先（最も安全側）で検証し、
 * 該当しなければ `http:` / `https:` の外部 URL として検証する。
 *
 * 外部 URL のみ許可で十分な箇所では `isSafeRedirectTo` を直接使うこと（内部パスを
 * 意図せず許可しないため）。
 *
 * @returns 内部パスなら正規化済み `pathname+search+hash`、外部 URL なら元の値。安全でなければ `null`。
 */
export function toSafeRedirectTarget(value: string | null | undefined): string | null {
  return toSafeInternalPath(value) ?? (isSafeRedirectTo(value) ? value : null);
}